import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const { PUBLIC_SITE_ROUTES, SITE_BASE_URL } = require('../site/siteRoutes');

type PublicRouteMetadata = {
  path: string;
  title: string;
  description: string;
};

const metadataByPath = new Map<string, PublicRouteMetadata>(
  (PUBLIC_SITE_ROUTES as PublicRouteMetadata[]).map((route) => [route.path, route])
);
const defaultImage = `${SITE_BASE_URL}/social-share-image.jpg`;
const pageManagedMetadataPaths = new Set([
  '/',
  '/sales',
  '/marketplace',
  '/sales-marketplace',
  '/pricing',
  '/ai-bot-builder-bootcamp',
  '/ai-bot-builder-bootcamp/self-paced',
  '/live-algo-lab',
  '/upgrade-to-pro',
  '/blog',
  '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds',
]);

function dynamicMetadata(pathname: string): PublicRouteMetadata | null {
  if (pathname.startsWith('/blog/')) {
    return {
      path: pathname,
      title: 'BotSpot Blog Article',
      description: 'Market analysis, AI trading insights, and product updates from BotSpot.',
    };
  }
  if (pathname.startsWith('/marketplace/strategy/')) {
    return {
      path: pathname,
      title: 'AI Trading Strategy | BotSpot Marketplace',
      description: 'Review strategy details, backtest results, and live trading history in the BotSpot marketplace.',
    };
  }
  return null;
}

export default function PublicRouteSeo() {
  const { pathname } = useLocation();
  const normalizedPathname = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  if (
    pageManagedMetadataPaths.has(normalizedPathname)
    || normalizedPathname.startsWith('/blog/')
    || normalizedPathname.startsWith('/marketplace/strategy/')
  ) return null;
  const metadata = metadataByPath.get(normalizedPathname) || dynamicMetadata(normalizedPathname);
  if (!metadata) return null;

  const canonical = `${SITE_BASE_URL}${normalizedPathname}`;
  return (
    <Helmet>
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:image" content={defaultImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
      <meta name="twitter:image" content={defaultImage} />
    </Helmet>
  );
}
